// src/services/billingService.js
import { db, auth } from './firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';

export const generateInvoiceNumber = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const timestamp = Date.now().toString().slice(-4);
  return `INV-${year}${month}${day}-${timestamp}`;
};

export const createInvoice = async (invoiceData) => {
  try {
    const vendorId = auth.currentUser?.uid;
    if (!vendorId) throw new Error('User not authenticated');

    // Validate invoice data
    if (!invoiceData.customerName || !invoiceData.items || invoiceData.items.length === 0) {
      throw new Error('Required invoice data is missing');
    }

    // Ensure proper calculation based on invoice type
    const subtotal = invoiceData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    let taxAmount = 0;
    let totalAmount = subtotal;

    if (invoiceData.invoiceType === 'with_gst' && invoiceData.taxRate > 0) {
      taxAmount = (subtotal * invoiceData.taxRate) / 100;
      totalAmount = subtotal + taxAmount;
    }

    const invoice = {
      ...invoiceData,
      vendorId,
      invoiceNumber: generateInvoiceNumber(),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      status: 'draft',
      subtotal,
      taxAmount,
      totalAmount,
      // Ensure invoiceType defaults to 'with_gst' if not specified
      invoiceType: invoiceData.invoiceType || 'with_gst'
    };

    const docRef = await addDoc(collection(db, 'invoices'), invoice);
    return { id: docRef.id, ...invoice };
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

export const getInvoices = async (filters = {}) => {
  try {
    const vendorId = auth.currentUser?.uid;
    if (!vendorId) throw new Error('User not authenticated');

    let q = query(
      collection(db, 'invoices'),
      where('vendorId', '==', vendorId),
      orderBy('createdAt', 'desc')
    );

    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    if (filters.invoiceType) {
      q = query(q, where('invoiceType', '==', filters.invoiceType));
    }

    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

export const getBillingStats = async (dateRange = {}) => {
  try {
    const vendorId = auth.currentUser?.uid;
    if (!vendorId) throw new Error('User not authenticated');

    let q = query(
      collection(db, 'invoices'),
      where('vendorId', '==', vendorId)
    );

    if (dateRange.start && dateRange.end) {
      q = query(
        q,
        where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
      );
    }

    const snapshot = await getDocs(q);
    const invoices = snapshot.docs.map(doc => doc.data());

    const stats = {
      totalInvoices: invoices.length,
      totalAmount: invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0),
      paidAmount: invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.totalAmount || 0), 0),
      pendingAmount: invoices.filter(inv => ['sent', 'draft'].includes(inv.status)).reduce((sum, inv) => sum + (inv.totalAmount || 0), 0),
      overdueAmount: invoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + (inv.totalAmount || 0), 0),
      gstAmount: invoices.filter(inv => inv.invoiceType === 'with_gst').reduce((sum, inv) => sum + (inv.taxAmount || 0), 0),
      nonGstAmount: invoices.filter(inv => inv.invoiceType === 'without_gst').reduce((sum, inv) => sum + (inv.totalAmount || 0), 0),
      statusBreakdown: {
        draft: invoices.filter(inv => inv.status === 'draft').length,
        sent: invoices.filter(inv => inv.status === 'sent').length,
        paid: invoices.filter(inv => inv.status === 'paid').length,
        overdue: invoices.filter(inv => inv.status === 'overdue').length,
        cancelled: invoices.filter(inv => inv.status === 'cancelled').length
      },
      typeBreakdown: {
        with_gst: invoices.filter(inv => inv.invoiceType === 'with_gst').length,
        without_gst: invoices.filter(inv => inv.invoiceType === 'without_gst').length
      }
    };

    return stats;
  } catch (error) {
    console.error('Error fetching billing stats:', error);
    throw error;
  }
};

export const getInvoiceById = async (invoiceId) => {
  try {
    const vendorId = auth.currentUser?.uid;
    if (!vendorId) throw new Error('User not authenticated');

    const docRef = doc(db, 'invoices', invoiceId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const invoiceData = { id: docSnap.id, ...docSnap.data() };
    
    // Verify this invoice belongs to the current vendor
    if (invoiceData.vendorId !== vendorId) {
      throw new Error('Unauthorized access to invoice');
    }
    
    return invoiceData;
  } catch (error) {
    console.error('Error fetching invoice:', error);
    throw error;
  }
};

export const updateInvoiceStatus = async (invoiceId, status, note = '') => {
  try {
    const vendorId = auth.currentUser?.uid;
    if (!vendorId) throw new Error('User not authenticated');

    const updateData = {
      status,
      updatedAt: Timestamp.now(),
      statusHistory: arrayUnion({
        status,
        note,
        updatedAt: Timestamp.now(),
        updatedBy: vendorId
      })
    };

    // Add specific fields for certain statuses
    if (status === 'sent') {
      updateData.sentAt = Timestamp.now();
    } else if (status === 'paid') {
      updateData.paidAt = Timestamp.now();
    }

    const docRef = doc(db, 'invoices', invoiceId);
    await updateDoc(docRef, updateData);
    
    return true;
  } catch (error) {
    console.error('Error updating invoice status:', error);
    throw error;
  }
};

export const sendInvoiceEmail = async (invoiceId) => {
  try {
    // This would integrate with your email service
    // For now, we'll just update the status to 'sent'
    await updateInvoiceStatus(invoiceId, 'sent', 'Invoice sent via email');
    
    // Here you would typically call your email service API
    // Example: await emailService.sendInvoice(invoiceData);
    
    return true;
  } catch (error) {
    console.error('Error sending invoice email:', error);
    throw error;
  }
};

export const generateInvoicePDF = async (invoice) => {
  try {
    // This would generate and download a PDF
    // You can use libraries like jsPDF or html2canvas
    
    // For now, we'll create a simple download trigger
    const element = document.createElement('a');
    const file = new Blob([JSON.stringify(invoice, null, 2)], {
      type: 'application/json'
    });
    element.href = URL.createObjectURL(file);
    element.download = `invoice-${invoice.invoiceNumber}.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

export const getRevenueData = async (options = {}) => {
  try {
    const vendorId = auth.currentUser?.uid;
    if (!vendorId) throw new Error('User not authenticated');

    let q = query(
      collection(db, 'invoices'),
      where('vendorId', '==', vendorId),
      orderBy('createdAt', 'desc')
    );

    if (options.start && options.end) {
      q = query(
        q,
        where('createdAt', '>=', Timestamp.fromDate(options.start)),
        where('createdAt', '<=', Timestamp.fromDate(options.end))
      );
    }

    const snapshot = await getDocs(q);
    const invoices = snapshot.docs.map(doc => doc.data());

    // Group data by period (daily, weekly, monthly, quarterly)
    const groupedData = {};
    
    invoices.forEach(invoice => {
      const date = invoice.createdAt.toDate();
      let period;

      switch (options.type) {
        case 'daily':
          period = date.toISOString().split('T')[0];
          break;
        case 'weekly':
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          period = weekStart.toISOString().split('T')[0];
          break;
        case 'quarterly':
          const quarter = Math.floor(date.getMonth() / 3) + 1;
          period = `${date.getFullYear()}-Q${quarter}`;
          break;
        default: // monthly
          period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }

      if (!groupedData[period]) {
        groupedData[period] = {
          period,
          revenue: 0,
          paid: 0,
          pending: 0,
          invoiceCount: 0,
          gstAmount: 0,
          nonGstAmount: 0
        };
      }

      groupedData[period].revenue += invoice.totalAmount || 0;
      groupedData[period].invoiceCount += 1;

      // Track GST and non-GST amounts
      if (invoice.invoiceType === 'with_gst') {
        groupedData[period].gstAmount += invoice.totalAmount || 0;
      } else {
        groupedData[period].nonGstAmount += invoice.totalAmount || 0;
      }

      if (invoice.status === 'paid') {
        groupedData[period].paid += invoice.totalAmount || 0;
      } else {
        groupedData[period].pending += invoice.totalAmount || 0;
      }
    });

    return Object.values(groupedData).sort((a, b) => a.period.localeCompare(b.period));
  } catch (error) {
    console.error('Error fetching revenue data:', error);
    throw error;
  }
};

export const getTopCustomers = async (options = {}) => {
  try {
    const vendorId = auth.currentUser?.uid;
    if (!vendorId) throw new Error('User not authenticated');

    let q = query(
      collection(db, 'invoices'),
      where('vendorId', '==', vendorId)
    );

    if (options.start && options.end) {
      q = query(
        q,
        where('createdAt', '>=', Timestamp.fromDate(options.start)),
        where('createdAt', '<=', Timestamp.fromDate(options.end))
      );
    }

    const snapshot = await getDocs(q);
    const invoices = snapshot.docs.map(doc => doc.data());

    // Group by customer
    const customerData = {};
    
    invoices.forEach(invoice => {
      const customerKey = invoice.customerEmail || invoice.customerName;
      
      if (!customerData[customerKey]) {
        customerData[customerKey] = {
          name: invoice.customerName,
          email: invoice.customerEmail,
          phone: invoice.customerPhone,
          totalAmount: 0,
          paidAmount: 0,
          invoiceCount: 0,
          gstInvoices: 0,
          nonGstInvoices: 0,
          lastInvoiceDate: invoice.createdAt
        };
      }

      customerData[customerKey].totalAmount += invoice.totalAmount || 0;
      customerData[customerKey].invoiceCount += 1;

      // Track invoice types
      if (invoice.invoiceType === 'with_gst') {
        customerData[customerKey].gstInvoices += 1;
      } else {
        customerData[customerKey].nonGstInvoices += 1;
      }

      if (invoice.status === 'paid') {
        customerData[customerKey].paidAmount += invoice.totalAmount || 0;
      }

      // Update last invoice date if this one is more recent
      if (invoice.createdAt > customerData[customerKey].lastInvoiceDate) {
        customerData[customerKey].lastInvoiceDate = invoice.createdAt;
      }
    });

    // Convert to array and sort by total amount
    return Object.values(customerData)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 10); // Top 10 customers
  } catch (error) {
    console.error('Error fetching top customers:', error);
    throw error;
  }
};

// Additional utility functions for GST reporting
export const getGSTReport = async (dateRange = {}) => {
  try {
    const vendorId = auth.currentUser?.uid;
    if (!vendorId) throw new Error('User not authenticated');

    let q = query(
      collection(db, 'invoices'),
      where('vendorId', '==', vendorId),
      where('invoiceType', '==', 'with_gst')
    );

    if (dateRange.start && dateRange.end) {
      q = query(
        q,
        where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
      );
    }

    const snapshot = await getDocs(q);
    const gstInvoices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Group by GST rate
    const gstBreakdown = {};
    let totalGstCollected = 0;
    let totalTaxableAmount = 0;

    gstInvoices.forEach(invoice => {
      const rate = invoice.taxRate || 0;
      if (!gstBreakdown[rate]) {
        gstBreakdown[rate] = {
          rate,
          taxableAmount: 0,
          taxAmount: 0,
          invoiceCount: 0
        };
      }

      gstBreakdown[rate].taxableAmount += invoice.subtotal || 0;
      gstBreakdown[rate].taxAmount += invoice.taxAmount || 0;
      gstBreakdown[rate].invoiceCount += 1;

      totalGstCollected += invoice.taxAmount || 0;
      totalTaxableAmount += invoice.subtotal || 0;
    });

    return {
      gstBreakdown: Object.values(gstBreakdown),
      totalGstCollected,
      totalTaxableAmount,
      totalInvoices: gstInvoices.length,
      invoices: gstInvoices
    };
  } catch (error) {
    console.error('Error fetching GST report:', error);
    throw error;
  }
};

export const updateInvoice = async (invoiceId, updateData) => {
  try {
    const vendorId = auth.currentUser?.uid;
    if (!vendorId) throw new Error('User not authenticated');

    // Recalculate totals if items or invoice type changed
    if (updateData.items || updateData.invoiceType || updateData.taxRate) {
      const subtotal = updateData.items ? 
        updateData.items.reduce((sum, item) => sum + (item.amount || 0), 0) : 
        updateData.subtotal;
      
      let taxAmount = 0;
      let totalAmount = subtotal;

      if (updateData.invoiceType === 'with_gst' && updateData.taxRate > 0) {
        taxAmount = (subtotal * updateData.taxRate) / 100;
        totalAmount = subtotal + taxAmount;
      }

      updateData.subtotal = subtotal;
      updateData.taxAmount = taxAmount;
      updateData.totalAmount = totalAmount;
    }

    updateData.updatedAt = Timestamp.now();

    const docRef = doc(db, 'invoices', invoiceId);
    await updateDoc(docRef, updateData);
    
    return true;
  } catch (error) {
    console.error('Error updating invoice:', error);
    throw error;
  }
};


// Add this to src/services/billingService.js

export const createQuotation = async (quotationData) => {
  try {
    const docRef = await addDoc(collection(db, 'quotations'), {
      ...quotationData,
      createdAt: quotationData.createdAt || Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date())
    });
    
    return {
      id: docRef.id,
      ...quotationData
    };
  } catch (error) {
    console.error('Error creating quotation:', error);
    throw new Error('Failed to create quotation: ' + error.message);
  }
};